/**
 * Style Notes API (#855). One resolved instruction.
 * Draft / reject / direction-note / save-to-library / promote.
 * Save instruction persists the one text even when no card is selected yet.
 */
import { api } from './client';

function isStyleCardsPayload(value) {
  if (!value || typeof value !== 'object') return false;
  return (
    Array.isArray(value.cards)
    || Array.isArray(value.sections)
    || Array.isArray(value.chapters)
    || Array.isArray(value.scenes)
    || Array.isArray(value.character_cards)
    || Array.isArray(value.people_for_voice)
    || Array.isArray(value.people_for_pov)
    || value.resolved_text != null
    || value.from_character_card === true
  );
}

export function unwrapStyleCards(res) {
  if (!res || typeof res !== 'object') return res;
  if (isStyleCardsPayload(res)) return res;
  const inner = res.data;
  if (inner && typeof inner === 'object') {
    if (isStyleCardsPayload(inner)) return inner;
    if (inner.data && typeof inner.data === 'object') return inner.data;
    return inner;
  }
  return res;
}

export function outlineChapters(outline) {
  /** Real chapter dividers, or none. An empty chapters list stays empty. */
  if (!outline || typeof outline !== 'object') return [];
  const named = Array.isArray(outline.chapters) ? outline.chapters : null;
  const fromSections = (outline.sections || []).filter((s) => s.section_type === 'chapter');
  if (named && named.length > 0) return named;
  if (named && named.length === 0 && fromSections.length === 0) return [];
  return fromSections;
}

export function outlineScenes(outline) {
  /** Scene pickers already work. Prefer the scenes list; do not invent. */
  if (!outline || typeof outline !== 'object') return [];
  const named = Array.isArray(outline.scenes) ? outline.scenes : null;
  const fromSections = (outline.sections || []).filter((s) => s.section_type === 'scene');
  if (named && named.length > 0) return named;
  if (named && named.length === 0 && fromSections.length === 0) return [];
  return fromSections;
}

function mergePeople(outline) {
  /** Cards first; same name keeps the entity id. Does not invent names. */
  const byKey = {};
  const fromApi = (
    (outline && Array.isArray(outline.people_for_pov) && outline.people_for_pov)
    || (outline && Array.isArray(outline.people_for_voice) && outline.people_for_voice)
    || []
  );
  for (const person of fromApi) {
    const name = (person.name || '').trim();
    if (!name) continue;
    const bibleId = person.bible_entry_id || '';
    const entityId = person.character_entity_id || '';
    byKey[name.toLowerCase()] = {
      pickId: bibleId ? `card:${bibleId}` : `entity:${entityId || person.id}`,
      name,
      bible_entry_id: bibleId,
      character_entity_id: entityId,
      thin: Boolean(person.thin),
    };
  }
  for (const card of outline?.character_cards || []) {
    const name = (card.name || '').trim();
    if (!name) continue;
    const prev = byKey[name.toLowerCase()] || {
      pickId: `card:${card.id}`,
      name,
      bible_entry_id: card.id,
      character_entity_id: '',
      thin: Boolean(card.thin),
    };
    byKey[name.toLowerCase()] = {
      ...prev,
      bible_entry_id: prev.bible_entry_id || card.id,
      pickId: prev.bible_entry_id || card.id ? `card:${prev.bible_entry_id || card.id}` : prev.pickId,
      thin: prev.thin || Boolean(card.thin),
    };
  }
  for (const ent of outline?.characters || []) {
    const name = (ent.name || '').trim();
    if (!name) continue;
    const entityId = ent.character_entity_id || ent.id;
    const prev = byKey[name.toLowerCase()] || {
      pickId: `entity:${entityId}`,
      name,
      bible_entry_id: ent.bible_entry_id || '',
      thin: false,
    };
    byKey[name.toLowerCase()] = {
      ...prev,
      character_entity_id: entityId || prev.character_entity_id || '',
      bible_entry_id: prev.bible_entry_id || ent.bible_entry_id || '',
    };
  }
  return Object.values(byKey).sort((a, b) => a.name.localeCompare(b.name));
}

export function peopleForVoice(outline) {
  /** Confirmed Character notes + Whose-head people. Generate needs a card. */
  return mergePeople(outline).filter((p) => p.bible_entry_id);
}

export function peopleForPov(outline) {
  /**
   * Whose head + Voice tied to a person. Same union as generate-voice,
   * including Character notes when story_entities is still empty.
   */
  return mergePeople(outline).map((p) => ({
    ...p,
    pickId: p.character_entity_id || p.bible_entry_id || p.pickId,
  }));
}

export async function listStyleCards(storyId, { includeRejected = false, signal } = {}) {
  const query = includeRejected ? '?include_rejected=true' : '';
  return unwrapStyleCards(await api.get(
    `/stories/${storyId}/style-cards${query}`,
    signal ? { signal } : undefined,
  ));
}

export async function getEffectiveStyleCard(storyId, { chapterId, sceneId } = {}) {
  const params = new URLSearchParams();
  if (chapterId) params.set('chapter_id', chapterId);
  if (sceneId) params.set('scene_id', sceneId);
  const q = params.toString() ? `?${params}` : '';
  return unwrapStyleCards(await api.get(`/stories/${storyId}/style-cards/effective${q}`));
}

export async function listStyleOutline(storyId, { signal } = {}) {
  return unwrapStyleCards(await api.get(
    `/stories/${storyId}/style-cards/outline`,
    signal ? { signal } : undefined,
  ));
}

export async function draftStyleCard(storyId, body) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-cards/draft`, body));
}

export async function generateStyleCardFromCharacter(storyId, body) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-cards/generate-from-card`, {
    bible_entry_id: body.bible_entry_id || null,
    character_entity_id: body.character_entity_id || null,
    name: body.name || null,
  }));
}

export async function saveStyleCardNotes(storyId, body) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-cards/save-notes`, {
    card_id: body.card_id || null,
    exemplars: body.exemplars ?? '',
    mechanics: body.mechanics ?? '',
    lexicon: body.lexicon ?? '',
    tone: body.tone ?? '',
    direction_note: body.direction_note ?? '',
  }));
}

export async function saveStyleCardInstruction(storyId, body) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-cards/save-instruction`, {
    name: body.name || '',
    resolved_text: body.resolved_text,
    direction_note: body.direction_note || '',
    character_entity_id: body.character_entity_id || null,
    card_id: body.card_id || null,
    exemplars: body.exemplars ?? '',
    mechanics: body.mechanics ?? '',
    lexicon: body.lexicon ?? '',
    tone: body.tone ?? '',
  }));
}

export async function updateStyleCard(storyId, cardId, body) {
  return unwrapStyleCards(await api.patch(`/stories/${storyId}/style-cards/${cardId}`, body));
}

export async function rejectStyleCard(storyId, cardId) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-cards/${cardId}/reject`));
}

export async function saveStyleCardToLibrary(storyId, cardId) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-cards/${cardId}/save-to-library`));
}

export async function promoteStyleCard(storyId, cardId, { target = 'book', sectionId } = {}) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-cards/${cardId}/promote`, {
    target,
    section_id: sectionId || null,
  }));
}

export async function copyStyleCardForScene(storyId, { sceneId, sourceCardId } = {}) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-cards/copy-for-scene`, {
    scene_id: sceneId,
    source_card_id: sourceCardId || null,
  }));
}

export async function setBookPov(storyId, {
  povMode,
  povEntityId,
  bibleEntryId,
  povDeferred = false,
  povCamera = null,
  povCast = [],
} = {}) {
  return unwrapStyleCards(await api.put(`/stories/${storyId}/style/pov`, {
    pov_mode: povDeferred ? null : (povMode || null),
    pov_entity_id: povEntityId || null,
    bible_entry_id: bibleEntryId || null,
    pov_deferred: Boolean(povDeferred),
    pov_camera: povCamera || null,
    pov_cast: povCast || [],
  }));
}

export async function setSectionStyle(storyId, sectionId, body) {
  return unwrapStyleCards(await api.put(`/stories/${storyId}/style/sections/${sectionId}`, body));
}

export async function createBlankStyle(storyId) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-room/styles`, {}));
}

export async function createStyleFromPreset(storyId, presetId) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-room/styles/from-preset`, {
    preset: presetId,
  }));
}

export async function createStyleFromWriting(storyId, { name, exemplars } = {}) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-room/styles/from-writing`, {
    name: name || '',
    exemplars: exemplars || '',
  }));
}

export async function copySavedStyle(storyId, cardId) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-room/styles/${cardId}/copy`, {}));
}

export async function saveOpenStyle(storyId, cardId, body) {
  const payload = {
    name: body.name || '',
    resolved_text: body.resolved_text ?? '',
    exemplars: body.exemplars ?? '',
  };
  if (body.dials !== undefined) payload.dials = body.dials || {};
  return unwrapStyleCards(await api.put(`/stories/${storyId}/style-room/styles/${cardId}`, payload));
}

export async function setBookStyle(storyId, cardId) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-room/styles/${cardId}/book`, {}));
}

export async function listStyleScenes(storyId, cardId) {
  return unwrapStyleCards(await api.get(`/stories/${storyId}/style-room/styles/${cardId}/scenes`));
}

export async function clearSceneStyle(storyId, sceneId) {
  return unwrapStyleCards(await api.post(`/stories/${storyId}/style-room/scenes/${sceneId}/clear`, {}));
}

export async function clearStyleScenes(storyId, cardId) {
  return unwrapStyleCards(await api.post(
    `/stories/${storyId}/style-room/styles/${cardId}/clear-scenes`,
    {},
  ));
}

export async function restoreStyleScenes(storyId, cardId, sceneIds) {
  return unwrapStyleCards(await api.post(
    `/stories/${storyId}/style-room/styles/${cardId}/restore-scenes`,
    { scene_ids: sceneIds || [] },
  ));
}

export async function deleteSavedStyle(storyId, cardId) {
  return unwrapStyleCards(await api.delete(`/stories/${storyId}/style-room/styles/${cardId}`));
}

export const styleCardsApi = {
  list: listStyleCards,
  effective: getEffectiveStyleCard,
  outline: listStyleOutline,
  draft: draftStyleCard,
  generateFromCard: generateStyleCardFromCharacter,
  saveNotes: saveStyleCardNotes,
  saveInstruction: saveStyleCardInstruction,
  update: updateStyleCard,
  reject: rejectStyleCard,
  saveToLibrary: saveStyleCardToLibrary,
  promote: promoteStyleCard,
  copyForScene: copyStyleCardForScene,
  setBookPov,
  setSectionStyle,
  createBlank: createBlankStyle,
  createFromPreset: createStyleFromPreset,
  createFromWriting: createStyleFromWriting,
  copyStyle: copySavedStyle,
  saveOpen: saveOpenStyle,
  setBookStyle,
  listScenes: listStyleScenes,
  clearSceneStyle,
  clearScenes: clearStyleScenes,
  restoreScenes: restoreStyleScenes,
  deleteStyle: deleteSavedStyle,
};
